import React from "react"

export function DesignGalleryModal({
  designs,
  designContext,
  selectedStudio,
  isAdmin,
  currentUserId,
  designThumbnails,
  editingDesignName,
  editingDesignNameValue,
  setEditingDesignNameValue,
  onStartEditingName,
  onFinishEditingName,
  onCancelEditingName,
  onLoadDesign,
  onDeleteDesign,
  onSetPreferredDesign,
  onClose,
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {designContext === "studio" && selectedStudio ? `${selectedStudio.name} Designs` : "My Designs"}
              </h2>
              {designContext === "studio" && selectedStudio && (
                <p className="text-sm text-gray-600 mt-1">
                  {isAdmin ? "Viewing all studio designs (Admin access)" : "Viewing your designs in this studio"}
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {designs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No designs saved yet.</p>
              <p className="text-sm text-gray-400 mt-2">Create and save your first design to see it here!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {designs.map(design => (
                <DesignCard
                  key={design.id}
                  design={design}
                  designs={designs}
                  designContext={designContext}
                  currentUserId={currentUserId}
                  designThumbnails={designThumbnails}
                  editingDesignName={editingDesignName}
                  editingDesignNameValue={editingDesignNameValue}
                  setEditingDesignNameValue={setEditingDesignNameValue}
                  onStartEditingName={onStartEditingName}
                  onFinishEditingName={onFinishEditingName}
                  onCancelEditingName={onCancelEditingName}
                  onLoadDesign={onLoadDesign}
                  onDeleteDesign={onDeleteDesign}
                  onSetPreferredDesign={onSetPreferredDesign}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function DesignCard({
  design,
  designs,
  designContext,
  currentUserId,
  designThumbnails,
  editingDesignName,
  editingDesignNameValue,
  setEditingDesignNameValue,
  onStartEditingName,
  onFinishEditingName,
  onCancelEditingName,
  onLoadDesign,
  onDeleteDesign,
  onSetPreferredDesign,
}) {
  const anotherDesignSubmitted = designs?.some(d => d.isPreferred && d.id !== design.id && d.createdBy === currentUserId)
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      {/* Thumbnail Preview */}
      <div className="mb-3 bg-gray-50 rounded-lg p-2 flex justify-center">
        {designThumbnails[design.id] ? (
          <img
            src={designThumbnails[design.id]}
            alt={`Preview of ${design.name || "design"}`}
            className="max-w-full max-h-32 object-contain rounded border border-gray-200"
          />
        ) : (
          <div className="w-32 h-20 bg-gray-200 rounded border border-gray-300 flex items-center justify-center">
            <div className="text-gray-400 text-xs">Loading...</div>
          </div>
        )}
      </div>
      <div className="mb-3">
        {editingDesignName === design.id ? (
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={editingDesignNameValue}
              onChange={e => setEditingDesignNameValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") onFinishEditingName()
                if (e.key === "Escape") onCancelEditingName()
              }}
              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <button onClick={onFinishEditingName} className="text-green-600 hover:text-green-700 flex-shrink-0" title="Save name">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button onClick={onCancelEditingName} className="text-red-600 hover:text-red-700 flex-shrink-0" title="Cancel">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 truncate">
              {design.name ||
                `Design ${design.id.split("_")[1] ? new Date(parseInt(design.id.split("_")[1])).toLocaleDateString() : "Unknown"}`}
            </h3>
            <button
              onClick={() => onStartEditingName(design)}
              className="ml-2 text-gray-400 hover:text-blue-500 flex-shrink-0"
              title="Rename design"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536M9 11l6.586-6.586a2 2 0 112.828 2.828L11.828 13.828a2 2 0 01-2.828 0L9 13V11z"
                />
              </svg>
            </button>
          </div>
        )}
        <p className="text-sm text-gray-500">{design.elements?.length || 0} elements</p>
        <p className="text-xs text-gray-400">
          Updated: {design.updatedAt?.toDate ? design.updatedAt.toDate().toLocaleString() : "Unknown"}
        </p>
        {designContext === "studio" && design.isPreferred && (
          <div className="mt-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              ✓ Submitted to Studio
            </span>
          </div>
        )}
      </div>
      <div className="flex space-x-2">
        <button
          onClick={() => onLoadDesign(design.id)}
          className="flex-1 bg-blue-500 text-white px-3 py-2 rounded text-sm hover:bg-blue-600 transition-colors"
        >
          Load
        </button>
        {designContext === "studio" && design.createdBy === currentUserId && (
          <button
            onClick={() => onSetPreferredDesign(design.id)}
            disabled={!design.isPreferred && anotherDesignSubmitted}
            title={!design.isPreferred && anotherDesignSubmitted ? "Withdraw your current submission first" : ""}
            className={`flex-1 px-3 py-2 rounded text-sm transition-colors ${
              design.isPreferred
                ? "bg-green-500 text-white hover:bg-green-600"
                : anotherDesignSubmitted
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {design.isPreferred ? "✓ Submitted (tap to withdraw)" : "Submit Ad to Studio"}
          </button>
        )}
        <button
          onClick={() => onDeleteDesign(design.id)}
          className="flex-1 bg-red-500 text-white px-3 py-2 rounded text-sm hover:bg-red-600 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>
  )
}

